import { Component, ElementRef, forwardRef, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseErrorForm } from '@hub/common/BaseErrorForm';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap, filter } from 'rxjs/operators';
import { NgSelectComponent } from '@ng-select/ng-select';

export const CUSTOM_MULTI_TAG_VALUE_ACCESSOR2: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => FormMultiSelectComponent2),
	multi: true
};

interface SelectOption {
	value: string,
	label: string
}

const SEARCH_MIN_LENGTH = 2;

@Component({
	selector: 'fm-multiselect2',
	templateUrl: './form-multiselect2.component.html',
	styles: [
		`
		.icon-border {
			border: 1px solid #d9d9d9 !important;
		}
		.whate-space--pre-wrap {
    	white-space: pre-wrap;
		}
		`
	  ],
	providers: [CUSTOM_MULTI_TAG_VALUE_ACCESSOR2]
})
export class FormMultiSelectComponent2 extends BaseErrorForm implements OnInit, ControlValueAccessor {

	constructor(private el: ElementRef,
		private renderer: Renderer2) {
		super();
	}

	@Input() options: SelectOption[] = [];
	@Input() bindLabel = 'label';
	@Input() bindValue;
	@Input() isCustomDescription: boolean = false;
	@Input() appendTo: string = null;
	@Input() groupBy = null;
	@Input() search$: (string) => Observable<SelectOption[]>;
	@Input() isAsync = false;
	@Input() maxSelectedItems = 50;
	@Input() bindUrlAddress = null;
	@Input() compareWith = (a: any, b: any) => { 
		return a === b || (
				(a.ConnectId || b.ConnectId) && a.ConnectId === b.ConnectId ||
				!(a.ConnectId || b.ConnectId) && 
				(a.value != undefined && a.value === b || a[this.bindLabel] === b[this.bindLabel])
			)
	};

	@ViewChild(NgSelectComponent) ngSelect;

	selectedItems: string[] = [];
	loading = false;
	input$: Subject<string>;
	items$: Observable<SelectOption[]>;
	placeholder = 'Type to search';

	ngOnInit() {
		if (this.isAsync) {
			this.input$ = new Subject<string>();
			this.setItems();
		}
		else {
			this.items$ = of(this.options);
		}
	}

	private setItems() {
		this.items$ = concat(of([]), // default items
			this.input$.pipe(
					filter(x => x && x.length >= SEARCH_MIN_LENGTH && (this.value && this.value.length ? this.value.length : 0) < this.maxSelectedItems), 
					debounceTime(200), 
					distinctUntilChanged(), 
					tap(() => this.loading = true), 
					switchMap(term => this.search$(term)
						.pipe(catchError(() => of([])), // empty list on error
									tap((x) => { this.loading = false; })
									)
					)
				)
			);
	}

	onOpen() {
		this.placeholder = '';
	}

	onClose() {
		this.placeholder = 'Type to search';
	}

	onFocus(event: Event) {
		setTimeout(() => {
			const element: HTMLElement = this.el.nativeElement.querySelector('input.ui-inputtext');
			if (element != null) {
				element.focus();
			}
		}, 0);
	}

	// functions for ngModel
	set value(val: string[]) {
		if (this.selectedItems !== val) {
			this.selectedItems = val;
			this.onChange(val);
		}
		if (this.isAsync) {
			this.setItems();
		}
	}

	get value(): string[] {
		return this.selectedItems;
	}

	get appendTarget(): string {
		return this.appendTo === 'body' ? null : this.appendTo;
	}

	writeValue(val: string[]) {
		this.selectedItems = val;
		this.onChange(val);
	}

	openUrlAddress(val: string) {
		if(val != null) {
		  window.open(val, "_blank");
		}
	}

	onChange = (_) => { };
	onTouched = () => { };

	registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
	registerOnTouched(fn: () => void): void { this.onTouched = fn; }
}
